// 集客画面の期間。日付はすべて日本時間の YYYY-MM-DD で扱う。

export type PeriodKind = "month" | "lastMonth" | "custom";

export interface Period {
  kind: PeriodKind;
  from: string;
  to: string;
  // 比較に使う直前の期間。今月なら前月の同じ日数、先月ならその前の月全体。
  compareFrom: string;
  compareTo: string;
  // 月単位の期間なら月の日数と経過日数。任意の期間では期間の日数と同じ値。
  daysTotal: number;
  daysElapsed: number;
  complete: boolean;
}

export function todayJst(now = new Date()): string {
  return new Date(now.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

function monthStart(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

function monthEnd(day: string): string {
  const date = new Date(`${monthStart(day)}T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + 1, 0);
  return date.toISOString().slice(0, 10);
}

function previousMonthStart(day: string): string {
  const date = new Date(`${monthStart(day)}T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() - 1);
  return date.toISOString().slice(0, 10);
}

export function buildPeriod(kind: PeriodKind, today: string, custom?: { from: string; to: string }): Period {
  if (kind === "month") {
    const from = monthStart(today);
    const daysElapsed = Number(today.slice(8, 10));
    const compareFrom = previousMonthStart(today);
    const compareEnd = monthEnd(compareFrom);
    const compareTo = addDays(compareFrom, daysElapsed - 1) > compareEnd ? compareEnd : addDays(compareFrom, daysElapsed - 1);
    return { kind, from, to: today, compareFrom, compareTo, daysTotal: daysBetween(from, monthEnd(today)), daysElapsed, complete: false };
  }
  if (kind === "lastMonth") {
    const from = previousMonthStart(today);
    const to = monthEnd(from);
    const compareFrom = previousMonthStart(from);
    const days = daysBetween(from, to);
    return { kind, from, to, compareFrom, compareTo: monthEnd(compareFrom), daysTotal: days, daysElapsed: days, complete: true };
  }
  const from = custom?.from ?? monthStart(today);
  const to = custom?.to && custom.to >= from ? custom.to : today;
  const days = daysBetween(from, to);
  return {
    kind,
    from,
    to,
    compareFrom: addDays(from, -days),
    compareTo: addDays(from, -1),
    daysTotal: days,
    daysElapsed: days,
    complete: to < today,
  };
}

export function formatDay(day: string): string {
  return `${Number(day.slice(5, 7))}月${Number(day.slice(8, 10))}日`;
}

export function formatShortDay(day: string): string {
  return `${Number(day.slice(5, 7))}/${Number(day.slice(8, 10))}`;
}

export function formatRange(from: string, to: string): string {
  if (from.slice(0, 7) === to.slice(0, 7)) return `${formatDay(from)}〜${Number(to.slice(8, 10))}日`;
  return `${formatDay(from)}〜${formatDay(to)}`;
}

export function listDays(from: string, to: string): string[] {
  const days: string[] = [];
  for (let day = from; day <= to; day = addDays(day, 1)) days.push(day);
  return days;
}
